// Feedback #832 — "to-do's due within the next ten days".
//
// ONE definition of "when is this to-do due" and "is it due soon", shared by
// the quick-access bar's To-Do badge/dropdown and the Tasks page's "Due within
// N days" filter.  They must agree on what "due" and "overdue" mean, or the
// bar and the page describe the same to-do differently.  (By design the badge
// counts only the NOT-yet-overdue part of the "Due within N days" tab — see
// QuickAccessBar.js — so the tab's number is the badge plus the past-due line.)
// Two copies of these predicates is how the two drift apart.
//
// What counts as a to-do: every row GET /api/calendar/tasks returns.  That
// endpoint is the Tasks page's data source and returns every event the caller
// can see (visibility 'everyone', created by them, or listed in visible_to),
// matter-visibility and per-matter-revocation filtered server-side.  So "the
// user's to-dos" here means exactly what the Tasks page lists — no more.
//
// Due date: `due_date` when set, else `start_time` — except a DEADLINE TO-DO
// that runs from one firm day to a later one, which is due on the later day.
// due_date is a Postgres DATE, which node-pg serializes as UTC midnight
// ("…T00:00:00.000Z"), so it MUST go through parseDateOnly — plain
// `new Date()` puts it on the previous day for any US-timezone user.
// start_time is a real instant.
//
// DEADLINE TO-DOS (James 2026-10-21, "Yes, due date everywhere"): Amicus
// stores a to-do from the day work starts to the day it is DUE, both at the
// firm's midnight, with due_date NULL — event 271119 "Benito v. Wilson - CMC
// Statement Due 10/22/26" runs 10/15 00:00 → 10/22 00:00 PT and is due 10/22.
// Dated by start_time it read "Overdue by 6 days" on 10/21, and Mark All
// Overdue Done could close it before it was due.  Which items are to-dos and
// their due day are NOT decided here: lib/eventDaySpan.js isDeadlineTodo /
// deadlineTodoDueDay (the printouts' rule, case table
// lib/testSupport/deadlineTodoCases.json), whose SQL twin is what the server's
// eventDueDaySql (api/src/lib/firmTime.js) uses for /dailies, /deadlines, the
// Dashboard's Overdue Tasks and complete-overdue.  A to-do due on a later day
// than it starts (laterDueDay below) is due on that day with no time of day
// (deadlineTodoTime gives none off its start day): overdue only once the day
// is over.  Everything else is dated exactly as before.  The rows must carry
// amicus_id, amicus_event_kind and end_time (GET /api/calendar, /tasks — the
// slim shape too — and /deadlines do); without them an item is not a to-do.
//
// "Days until due" is a CALENDAR-day difference in the FIRM's timezone, not
// a count of elapsed 24-hour periods.  The earlier version used date-fns
// differenceInDays, which truncates toward zero: at 3 PM a due_date of
// TOMORROW came out as 0 and was labelled "Today"; a deadline at 5 PM
// yesterday came out as 0 and counted as upcoming (amber) instead of overdue;
// and a due_date 11 calendar days out slipped inside "within 10 days".  Both
// sides are converted to the firm's wall clock first (toFirmWallClock), so an
// attorney opening the list from another timezone sees the firm's days — the
// #821 / Taiwan lesson.
//
// The Daily Snapshot (Dailies.js) and the Deadlines page use daysUntilDue and
// firmDueDay too (2026-10-17, James: "a task or event at 5 PM Pacific or later
// doesn't show as due today"), so their "Due today!" / "Today" chips, dates and
// buckets name the same firm day the server filtered on (eventDueDaySql in
// api/src/lib/firmTime.js).
//
// Overdue is a separate question from "which day": a timed to-do whose moment
// has already passed is overdue even when it falls earlier today (a 9 AM
// filing deadline is missed at 3 PM).  A date-only to-do — a due_date, a
// start_time at exactly firm-local midnight, which is how the Amicus import
// stores all-day items, or a to-do due on a later day than it starts — is not
// overdue until its whole day has passed.

import { differenceInCalendarDays } from 'date-fns'
import { parseDateOnly } from './format'
import { toFirmWallClock, firmDateIso } from './eventTime'
import { isDeadlineTodo, deadlineTodoDueDay } from '../lib/eventDaySpan'

export const DUE_SOON_DAYS = 10

// The Tasks page loads events from 6 × 30 days back (the server's default
// window).  The quick-access bar asks for the same lookback so an overdue
// to-do counted in the badge is also on the page it links to.
export const TODO_LOOKBACK_DAYS = 180

// 'YYYY-MM-DD' when `ev` is a deadline to-do with no due_date whose due day
// (the shared rule: end_time's firm date) is a LATER firm day than its start
// day — the Amicus to-do shape; else ''.  A due_date item keeps its due_date
// (as before); a to-do due on its own start day is dated by start_time as
// before (same day, and its start time IS its time that day).
function laterDueDay (ev) {
  if (!ev || ev.due_date || !ev.start_time || !isDeadlineTodo(ev)) return ''
  const day = deadlineTodoDueDay(ev)
  const startDay = firmDateIso(new Date(ev.start_time))
  return day && startDay && day > startDay ? day : ''
}

/**
 * An imported to-do due on a later day than it starts (see laterDueDay): its
 * start time is not a time on its due day, so pages show its due date and no
 * time of day.
 */
export function isDueAfterStartDay (ev) {
  return laterDueDay(ev) !== ''
}

/** The real instant the to-do is due (for sorting), or null when undated. */
export function todoDueDate (ev) {
  if (!ev) return null
  const raw = ev.due_date || ev.start_time
  if (!raw) return null
  // A to-do due on a later day: its end_time, an instant on that day (the
  // firm's midnight starting it, for an Amicus to-do).
  const d = ev.due_date
    ? parseDateOnly(ev.due_date)
    : new Date(laterDueDay(ev) ? ev.end_time : ev.start_time)
  return d instanceof Date && !isNaN(d.getTime()) ? d : null
}

// The due moment as the FIRM's wall clock (local fields = firm fields), plus
// whether it is a date-only (all-day) item.  Display / day-bucketing only.
function firmDue (ev) {
  if (!ev) return null
  if (ev.due_date) {
    const d = parseDateOnly(ev.due_date)
    return d instanceof Date && !isNaN(d.getTime()) ? { day: d, dateOnly: true } : null
  }
  if (!ev.start_time) return null
  const later = laterDueDay(ev)
  if (later) {
    const d = parseDateOnly(later)
    return d instanceof Date && !isNaN(d.getTime()) ? { day: d, dateOnly: true } : null
  }
  const d = toFirmWallClock(ev.start_time)
  if (!d || isNaN(d.getTime())) return null
  return { day: d, dateOnly: d.getHours() === 0 && d.getMinutes() === 0 }
}

/**
 * The firm-local day an item is due, as a Date whose LOCAL fields are the
 * firm's (a due_date, or a to-do's later due day, at local midnight; a timed
 * item at its firm wall-clock time), or null when undated.  For display and
 * day-bucketing only — never an instant to sort by or send back (use
 * todoDueDate for that).
 */
export function firmDueDay (ev) {
  const f = firmDue(ev)
  return f ? f.day : null
}

/** Firm calendar days until due (0 = today, negative = a past day), or null when undated. */
export function daysUntilDue (ev, now = new Date()) {
  const f = firmDue(ev)
  if (!f) return null
  const today = toFirmWallClock(now)
  if (!today) return null
  return differenceInCalendarDays(f.day, today)
}

/** Incomplete and past due: a past day, or a timed moment earlier today. */
export function isOverdue (ev, now = new Date()) {
  if (!ev || ev.is_completed) return false
  const n = daysUntilDue(ev, now)
  if (n === null) return false
  if (n < 0) return true
  if (n > 0) return false
  const f = firmDue(ev)
  if (f.dateOnly) return false
  return new Date(ev.start_time).getTime() < now.getTime()
}

/** Incomplete and due within `days` firm calendar days — overdue items included. */
export function isDueWithin (ev, days = DUE_SOON_DAYS, now = new Date()) {
  if (!ev || ev.is_completed) return false
  const n = daysUntilDue(ev, now)
  return n !== null && n <= days
}

/** Short label shared by the To-Do dropdown: "Overdue 3d", "Earlier today", "Today", "Tomorrow", "In 5 days". */
export function dueLabel (ev, now = new Date()) {
  const n = daysUntilDue(ev, now)
  if (n === null) return ''
  if (n < 0) return `Overdue ${Math.abs(n)}d`
  if (n === 0) return isOverdue(ev, now) ? 'Earlier today' : 'Today'
  if (n === 1) return 'Tomorrow'
  return `In ${n} days`
}

/**
 * Split a to-do list into what the quick-access bar shows.
 * Returns { overdue, upcoming, total } — each list sorted soonest-due first.
 */
export function dueSoonSummary (events, days = DUE_SOON_DAYS, now = new Date()) {
  const overdue = []
  const upcoming = []
  for (const ev of Array.isArray(events) ? events : []) {
    if (!isDueWithin(ev, days, now)) continue
    if (isOverdue(ev, now)) overdue.push(ev)
    else upcoming.push(ev)
  }
  const byDue = (a, b) => todoDueDate(a) - todoDueDate(b)
  overdue.sort(byDue)
  upcoming.sort(byDue)
  return { overdue, upcoming, total: overdue.length + upcoming.length }
}

/** Parse ?due=N from a URL.  Accepts 1–365; anything else → null. */
export function parseDueParam (value) {
  if (value === null || value === undefined || value === '') return null
  if (!/^\d+$/.test(String(value).trim())) return null
  const n = parseInt(value, 10)
  return n >= 1 && n <= 365 ? n : null
}
